import {
  ControleQuinquennalAiAnswer as ControleQuinquennalAiAnswerEntity,
  ControleQuinquennalAiResult as ControleQuinquennalAiResultEntity,
  ControleQuinquennalAxeStatus as ControleQuinquennalAxeStatusEntity,
  ControleQuinquennalAxeFileUploader as ControleQuinquennalAxeFileUploaderEntity,
  ControleQuinquennalAxeType as ControleQuinquennalAxeTypeEntity,
  ControleQuinquennalDimension as ControleQuinquennalDimensionEntity,
  ControleQuinquennalAxeFileContexteDepot as ControleQuinquennalAxeFileContexteDepotEntity,
  ControleQuinquennalClassificationStatus as ControleQuinquennalClassificationStatusEntity,
  TypePieceDeclaration as TypePieceDeclarationEntity,
  ControleQuinquennalStatus as ControleQuinquennalStatusEntity,
  ControleQuinquennalSujet as ControleQuinquennalSujetEntity,
  ControleQuinquennalVerdictSousAxe as ControleQuinquennalVerdictSousAxeEntity,
  OriasStatut as OriasStatutEntity,
  VerificationStatus as VerificationStatusEntity,
  AnalyseRunScope as AnalyseRunScopeEntity,
  ControleQuinquennalAnalyseRun as ControleQuinquennalAnalyseRunEntity,
  ControleQuinquennalAnalyseRunUsage as ControleQuinquennalAnalyseRunUsageEntity,
  ControleQuinquennalAnalyseRunRowInput as ControleQuinquennalAnalyseRunRowInputEntity,
  ControleQuinquennalCreateAnalyseRunsBody as ControleQuinquennalCreateAnalyseRunsBodyEntity,
  ControleQuinquennalCompleteAnalyseRunBody as ControleQuinquennalCompleteAnalyseRunBodyEntity,
  ControleQuinquennalCompleteAnalyseRunResult as ControleQuinquennalCompleteAnalyseRunResultEntity,
  OlsSums as OlsSumsEntity,
  AnalyseRunDurationSums as AnalyseRunDurationSumsEntity,
  AnalyseRunStats as AnalyseRunStatsEntity,
  EntrepriseDuControle as EntrepriseDuControleEntity,
} from '../service/controle-quinquennal.definition';

// Mirror of analyse ControleQuinquennalAnswer/resultPayload — keep in sync with analyse/src/app/controle_quinquennal/dto/cq.dto.ts
export type ControleQuinquennalAiAnswer = ControleQuinquennalAiAnswerEntity;
export type ControleQuinquennalAiResult = ControleQuinquennalAiResultEntity;
export type ControleQuinquennalDimension = ControleQuinquennalDimensionEntity;
export type ControleQuinquennalSujet = ControleQuinquennalSujetEntity;
export type ControleQuinquennalVerdictSousAxe =
  ControleQuinquennalVerdictSousAxeEntity;
export type ControleQuinquennalAxeFileContexteDepot =
  ControleQuinquennalAxeFileContexteDepotEntity;
export type ControleQuinquennalClassificationStatus =
  ControleQuinquennalClassificationStatusEntity;
export type TypePieceDeclaration = TypePieceDeclarationEntity;

// Estimation coût / durée analyse CQ (conception EN-1476).
export type AnalyseRunScope = AnalyseRunScopeEntity;
export const AnalyseRunScope = AnalyseRunScopeEntity;
export type ControleQuinquennalAnalyseRun = ControleQuinquennalAnalyseRunEntity;
export type ControleQuinquennalAnalyseRunUsage =
  ControleQuinquennalAnalyseRunUsageEntity;
export type ControleQuinquennalAnalyseRunRowInput =
  ControleQuinquennalAnalyseRunRowInputEntity;
export type ControleQuinquennalCreateAnalyseRunsBody =
  ControleQuinquennalCreateAnalyseRunsBodyEntity;
export type ControleQuinquennalCompleteAnalyseRunBody =
  ControleQuinquennalCompleteAnalyseRunBodyEntity;
export type ControleQuinquennalCompleteAnalyseRunResult =
  ControleQuinquennalCompleteAnalyseRunResultEntity;
export type OlsSums = OlsSumsEntity;
export type AnalyseRunDurationSums = AnalyseRunDurationSumsEntity;
export type AnalyseRunStats = AnalyseRunStatsEntity;
// Accès en pointillé sur l'Entity (le vrai `enum`), jamais sur le re-export
// `AnalyseRunScope` ci-dessus (const+type fusionnés : TS2702, pas un namespace).
export type ControleQuinquennalAnalyseRunResultStatus =
  VerificationStatusEntity.SUCCESS | VerificationStatusEntity.FAILED;

export type ControleQuinquennal = {
  id: string;
  idEntreprise: string;
  idEntreprisePrincipale: string;
  entrepriseName: string;
  verificateurId: string;
  millesime: number;
  status: ControleQuinquennalStatus;
  dateDepot: Date | null;
  dateCreation: Date;
  dateDebut: Date | null;
  dateFin: Date | null;
  idTicket: string;
  pipelineStage: string | null;
  /** Dernier statut ORIAS collecté, null tant qu'aucune collecte n'a eu lieu. */
  oriasStatut: OriasStatut | null;
  oriasCheckedAt: Date | null;
  /** Dernière mise en remédiation du contrôle, null s'il n'y en a jamais eu. */
  dateMiseEnRemediation: Date | null;
};
export type ControleQuinquennalCreation = Omit<
  ControleQuinquennal,
  | 'id'
  | 'status'
  | 'dateDepot'
  | 'dateDebut'
  | 'dateFin'
  | 'pipelineStage'
  | 'oriasStatut'
  | 'oriasCheckedAt'
  | 'dateMiseEnRemediation'
>;
export type ControleQuinquennalWithCompletion = ControleQuinquennal & {
  hasRemediations: boolean;
  completion: number;
};
export type ControleQuinquennalWithAxes = ControleQuinquennal & {
  controleQuinquennalAxes: ControleQuinquennalAxeWithNumberOfFiles[];
  datePremiereRemediation: Date | null;
  dateRapportFinal: Date | null;
  /**
   * Une analyse IA a déjà été lancée pour le dépôt courant : le lancement
   * dossier est refusé, seules les relances par axe restent possibles.
   */
  analyseDossierLancee: boolean;
};

export type ControleQuinquennalMetriques = {
  questionsRepondues: number;
  questionsTotal: number;
  /** Pourcentage d'axes conformes, null si aucun axe. */
  conformiteVerificateur: number | null;
  /**
   * Pourcentage de sous-axes suggérés conformes par l'IA parmi ceux qu'elle a
   * tranchés au dépôt courant, null tant qu'aucun verdict n'est disponible.
   */
  conformiteIa: number | null;
};

/** Le SIREN accompagne les métriques : il vient de la même résolution HubSpot. */
export type ControleQuinquennalMetriquesAvecSiren =
  ControleQuinquennalMetriques & { siren: string | null };

/**
 * Ligne de la liste admin : ni les axes ni les métriques n'y figurent. Les
 * métriques demandent d'ouvrir tous les questionnaires du contrôle et de ses
 * assujettis, et leur entreprise vient de HubSpot — elles sont servies à part
 * (`calculerMetriques`) pour que la liste s'affiche sans les attendre.
 */
export type ControleQuinquennalAdminListItem = ControleQuinquennal & {
  datePremiereRemediation: Date | null;
  dateRapportFinal: Date | null;
};

/**
 * Un contrôle et l'entreprise que la gateway lui a résolue depuis HubSpot,
 * `null` quand elle n'a rien trouvé — les questionnaires de ses assujettis
 * sortent alors du total, faute de SIREN pour les atteindre.
 */
export type EntrepriseDuControle = EntrepriseDuControleEntity;

export type ControleQuinquennalMetriquesRequestItem = {
  id: string;
  entreprise: EntrepriseDuControle | null;
};

export type OriasStatut = OriasStatutEntity;
export const OriasStatut = OriasStatutEntity;

/** Sirens à collecter → statut ORIAS relevé, tel que le job hebdomadaire le persiste. */
export type MajStatutsOriasRequest = {
  statutsBySiren: Record<string, OriasStatut>;
};

export type MajStatutsOriasResponse = {
  controlesMisAJour: number;
};

export type ControleQuinquennalAxeType = ControleQuinquennalAxeTypeEntity;
export const ControleQuinquennalAxeType = ControleQuinquennalAxeTypeEntity;
export type ControleQuinquennalStatus = ControleQuinquennalStatusEntity;
export const ControleQuinquennalStatus = ControleQuinquennalStatusEntity;
export type ControleQuinquennalAxeStatus = ControleQuinquennalAxeStatusEntity;
export const ControleQuinquennalAxeStatus = ControleQuinquennalAxeStatusEntity;
export type VerificationStatus = VerificationStatusEntity;
export const VerificationStatus = VerificationStatusEntity;
export {
  SOCIETE_AXE_SUBAXIS,
  STALE_PENDING_ANALYSIS_MS,
} from '../service/controle-quinquennal.definition';
export type ControleQuinquennalAxeVerificationProcessingResultStatus =
  VerificationStatusEntity.SUCCESS | VerificationStatusEntity.FAILED;

export type ControleQuinquennalAxe = {
  idAxe: string;
  type: ControleQuinquennalAxeType;
  idControleQuinquennal: string;
  isComplet: boolean;
  status: ControleQuinquennalAxeStatus;
  lastZipGeneratedAt: Date | null;
};
export type ControleQuinquennalAxeWithNumberOfFiles = ControleQuinquennalAxe & {
  numberOfFiles: number;
  hasRemediation: boolean;
  aiProcessingStatus: VerificationStatus | null;
};
export type ControleQuinquennalAxeCreation = Omit<
  ControleQuinquennalAxe,
  | 'idAxe'
  | 'isComplet'
  | 'status'
  | 'idControleQuinquennal'
  | 'lastZipGeneratedAt'
>;
export type ControleQuinquennalAxeUpdate = {
  isComplet: boolean;
  idFiles: string[];
  typesPieceDeclares?: TypePieceDeclaration[];
};

export type ControleQuinquennalAxeCreationAndDeletion = {
  creation: ControleQuinquennalAxeCreation[];
  idAxesDeletion: string[];
};

export type ControleQuinquennalAxeWithFilesAndTotal = {
  axe: ControleQuinquennalAxeWithFiles;
  total: number;
};

export type ControleQuinquennalAxeWithFiles = ControleQuinquennalAxe & {
  controleQuinquennalAxeFiles: ControleQuinquennalAxeFile[];
  aiResult?: ControleQuinquennalAiResult | null;
  // Statut/erreur de la dernière analyse IA de l'axe (dépôt courant), exposés
  // pour l'écran vérificateur (relance + suivi). Null quand aucune vérif chargée.
  processingStatus?: VerificationStatus | null;
  analysisError?: string | null;
  // Date de la 1ère mise en remédiation de l'axe (créationDate du plus ancien
  // ControleQuinquennalAxeRemediation). Null si l'axe n'a jamais été en remédiation.
  firstRemediationDate: Date | null;
  // Autorise ou non le bouton « Relancer l'analyse IA » : vrai si la dernière
  // analyse a échoué, est orpheline (PENDING périmé), ou si le vérificateur a
  // ajouté une pièce depuis. Absent hors feature IA. Voir ControleQuinquennalAxe.
  canRelaunchAnalysis?: boolean;
};
export type ControleQuinquennalAxeVerificationProcessing = {
  id: string;
  idAxe: string;
  dateDepot: Date;
  // '' = ligne d'axe société ; 'LISTE' ; '<assujettiId>:<DIMENSION>'.
  subAxis: string;
  assujettiId: string | null;
  dimension: string | null;
  status: VerificationStatus;
  startedAt: Date;
  endedAt: Date | null;
  errorMessage: string | null;
  resultPayload: unknown | null;
  preVerdict: unknown | null;
};
export type ControleQuinquennalAxeFileUploader =
  ControleQuinquennalAxeFileUploaderEntity;

export type ControleQuinquennalAxeFile = {
  idFile: string;
  pathname: string;
  filename: string;
  isReadonly: boolean;
  createdAt: Date;
  uploadBy: ControleQuinquennalAxeFileUploader | null;
  idRemediation: string | null;
  /** État du contrôle au dépôt de la pièce, figé à l'écriture. */
  contexteDepot: ControleQuinquennalAxeFileContexteDepot | null;
  /** Mise en remédiation du contrôle en vigueur au dépôt. */
  dateMiseEnRemediation: Date | null;
  /** Type déclaré par le déposant (slug de taxonomie). */
  typePieceDeclare: string | null;
  /** Type rendu par le classificateur ; n'écrase jamais le déclaré. */
  typePieceDetecte: string | null;
  classificationStatus: ControleQuinquennalClassificationStatus | null;
  taxonomieVersion: string | null;
  classifiedAt: Date | null;
};
export type ControleQuinquennalAxeVerificationProcessingUpdate = {
  dateDepot: string;
  // Optionnel : défaut '' (ligne d'axe société) — la gateway reste sur la forme
  // legacy tant que le fan-out (MR-E) n'envoie pas de sous-axe.
  subAxis?: string;
  status: ControleQuinquennalAxeVerificationProcessingResultStatus;
  errorMessage?: string;
  resultPayload?: unknown;
};
export type ControleQuinquennalRemediationRequestType = {
  description: string;
  idFiles: string[];
};
export type ControleQuinquennalRemediationFileResponseType = {
  idFile: string;
  pathname: string;
  filename: string;
};
export type ControleQuinquennalRemediationResponseType = {
  description: string;
  creationDate: string;
  id: string;
};

export type ControleQuinquennalRemediationWithFileResponseType =
  ControleQuinquennalRemediationResponseType & {
    files: ControleQuinquennalRemediationFileResponseType[];
  };
export type ControleQuinquennalRapport = {
  idFile: string;
  pathname: string;
  filename: string;
};

export type ControleQuinquennalSearchRequestType = {
  search?: string;
  sortField?: string;
  sortOrder?: 'asc' | 'desc' | undefined;
  page: number;
  limit: number;
  pipelineStages?: string[];
  /**
   * Portée des `compteurs` de la réponse, indépendante de `pipelineStages` :
   * les badges d'onglets du front doivent rester stables quel que soit
   * l'onglet affiché. Absent, les compteurs couvrent tous les stages.
   */
  compteursPipelineStages?: string[];
  /**
   * Propriétaire dont on compte les contrôles en cours (badge « mes contrôles »).
   * Distinct de `verificateurId`, qui restreint la liste elle-même : les deux
   * onglets doivent compter chacun sur sa propre portée.
   */
  compteurVerificateurId?: string;
  millesimes?: number[];
  verificateurId?: string;
  dateDebutFrom?: string;
  dateDebutTo?: string;
  dateDepotFrom?: string;
  dateDepotTo?: string;
  dateFinFrom?: string;
  dateFinTo?: string;
};

/** Compteurs des onglets de la liste admin, hors restriction de vue. */
export type ControleQuinquennalAdminCompteurs = {
  total: number;
  enCours: number;
  mesEnCours: number;
};

export type ControleQuinquennalCancelBatchRequestType = {
  ids: string[];
};

export type ControleQuinquennalCancelBatchFailure = {
  id: string;
  raison: string;
};

export type ControleQuinquennalCancelBatchResult = {
  annules: ControleQuinquennalCancelled[];
  echecs: ControleQuinquennalCancelBatchFailure[];
};

export type ControleQuinquennalCancelled = {
  id: string;
  idEntreprise: string;
  entrepriseName: string;
  millesime: number;
  dateInitiation: Date;
};
